import { ErrorCode, Country } from '../../types'
import { BankAccountDetails, CardBrand, CardExpiryStatus, PaymentType, CashAppPayDetail, Money } from '@afterpay/types'
import { DeviceDetails } from '../types'
import { Theme } from '@afterpaytouch/core'

export enum Cohort {
  NetNew = 'Net New',
  APOnly = 'Narwhal Only',
  CashOnly = 'Unicorn Only',
}
export interface PromoExclusion {
  campaignIds: string[]
  tags: string[]
  excludeAll: boolean
}

export interface StartCheckoutRequest {
  deviceDetails: DeviceDetails
  profilingSessionId: string
  promoExclusion?: PromoExclusion
  checkoutCohort?: Cohort
  checkoutTheme?: Theme
}

export type PortalApiHeaders = StartCheckoutHeaders

export interface StartCheckoutHeaders {
  'x-cash-token'?: string
  'X-Registration-Flow'?: string
}

export interface StartCheckoutQuery {
  token: string
  body: StartCheckoutRequest
  headers?: StartCheckoutHeaders
}

export interface OfferDetail {
  monthlyAmount: Money
  amountDueToday: Money
  totalAmount: Money
  numberOfInstallments: number
  apr?: string
  paymentType: PaymentType
  rejectedReasonCode?: ErrorCode
}

export interface DonationCampaignDetails {
  amount: Money
  campaignId: string
}

export interface Promotion {
  discount: Money
  owedAfterDiscount: Money
  orderDiscountedPaymentSchedules: CheckoutPaymentSchedule[]
  code: string
}

export enum FirstPaymentType {
  Unknown = '',
  VariablePaymentUpfront = 'vpuf',
  LargePaymentUpfront = 'lpuf',
  NoPaymentUpfront = 'npuf',
  PaymentUpfront = 'puf',
}

// TODO: consolidatewith PaymentSchedule interface
export interface CheckoutPaymentSchedule {
  amount: Money
  discount?: Money
  owedAfterDiscount?: Money
  installmentDueDate: string | null
}

export interface CardDetails {
  cardholderName: string
  maskedPan: string
  expiryYear: number
  expiryMonth: number
  expiryDate: string
  token: string
  cvvRequired: boolean
  preferred: boolean
  cardBrand: CardBrand
  countryCode: Country
  creditCardType?: string
  issuingBank: string
  cardExpiryStatus: CardExpiryStatus
  id?: number | null
  allowed?: boolean
}

export enum TermsDisclosureStatus {
  NEW_USER_NOT_ACCEPTED_TERMS = 'NEW_USER_NOT_ACCEPTED_TERMS',
  NEW_USER_ACCEPTED_TERMS = 'NEW_USER_ACCEPTED_TERMS',
  EXISTING_USER_TERMS_NOT_APPLICABLE = 'EXISTING_USER_TERMS_NOT_APPLICABLE',
}

export interface ConsumerLendingPromotion {
  id: string
  apr: number
  loadPeriodMonths: number[]
}

export interface AccountOpeningDisclosureConsent extends Record<PaymentType, boolean> {}

export enum ConsumerFeeType {
  CONSUMER_FINANCE_FEE = 'CONSUMER_FINANCE_FEE',
}

export interface ConsumerFeeItem {
  type: ConsumerFeeType
  variableRate: number
  variableAmount: Money
  apr: number
}

type RiskLevel = 'RISK_LEVEL_UNKNOWN' | 'LOW_RISK' | 'HIGH_RISK'

export interface DownPaymentRiskInfo {
  adjustedDownPaymentEligible: boolean
  downPaymentRiskLevel: RiskLevel
}

export interface StartedCheckout {
  traceId: string
  hashedTraceId: string
  preferredCard: CardDetails | null
  preferredBankAccount: BankAccountDetails | null
  cashAppPay?: CashAppPayDetail[]
  savedCards: CardDetails[]
  savedBankAccounts: BankAccountDetails[]
  paymentSchedules: CheckoutPaymentSchedule[]
  amountDueToday: Money
  paymentUpFront: boolean
  statusCode: number
  minimumOrderValue?: Money
  consumerAmount: Money
  consumerAmountWithFees?: Money
  merchantAmount: Money
  exchangeRate: string
  downPaymentAmount: Money
  downPaymentType: FirstPaymentType
  installmentAmount: Money
  promotion: Promotion | null
  firstLateFeeData?: {
    variable: string
    fixedAmount: Money
    amountInCheckout: boolean
    zeroLateFee: boolean
  }
  purchasePaymentAgreementDocumentUrl?: string
  purchasePaymentAgreementDocumentId?: string
  paymentRoutingKey: string
  donationCampaign?: string
  donationCampaignDetails?: DonationCampaignDetails
  enabledCardTypes: CardBrand[]
  chargeAtShip: boolean
  paymentInterval?: string
  preorder: boolean
  consumerPendingAmount?: Money
  loyaltyNpufEligible?: boolean
  otbAmount?: Money // won't be present until core 21.17 release
  offers?: OfferDetail[]
  termsDisclosureStatus?: TermsDisclosureStatus
  creditCheckConsent?: boolean
  consumerFees?: ConsumerFeeItem[]
  consumerLendingPromotion: ConsumerLendingPromotion | null
  accountOpeningDisclosureConsent?: AccountOpeningDisclosureConsent
  downPaymentRiskInfo?: DownPaymentRiskInfo
}
